import { useState, useEffect, Suspense, ReactFragment } from "react";
import {
  LocalizationConfig,
  LocalizationProvider,
  TranslationConsumer,
} from "@scuf/localization";
import { ToastProvider, useToasts } from "react-toast-notifications";
import "./App.scss";
import { Route, Routes, useNavigate } from "react-router-dom";
import { routesConfig } from "./routes";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Notification, Button } from "@scuf/common";
import AuthProvider from "./common/authentication/authService";
import { useDispatch, useSelector } from "react-redux";
import { setLoggedIn, setDownloadData } from "./features/store/action";
import { useTokenAutoRefresh } from "./common/authentication/useTokenAutoRefresh";
import { HubConnectionState } from "@microsoft/signalr";
import { AppState } from "./common/store/combinedReducer";
import { setUpSignalRConnection } from "./common/utility/Dashboard/utility";
import ToastMessageComponent from "./features/components/academy/common/components/toast/toast";
import {
  hideLoader,
  hideModel,
} from "./features/components/academy/store/model/model.action";
import { resetAbortController } from "./features/components/academy/common/interceptor/base.services";
import { handleDownload } from "./common/utility/dataManagementUtils";

interface IMessageDescriptor {
  id: string;
  defaultMessage: string;
}

export interface ITranslationObject {
  localizations: {};
}

export declare type TFunction = (
  key: string | IMessageDescriptor,
  option?: Record<string, any>,
) => string;

export let notifyToast: (
  content: string | JSX.Element,
  type: "warning" | "success" | "error" | "info",
) => void;

export let translateString: (value: string) => string | JSX.Element;

export const importLocals = (language: string) =>
  import(`./localization/localization_${language}`).catch(
    () => import("./localization/localization_enus"),
  );

export const ToastNotification = ({ closeToast, text, type }: any) => (
  <Notification
    className="toast-notification"
    severity={type}
    onCloseClick={closeToast}
    hasIcon={false}
    title={text}
  />
);
export let customToast = (
  content: string | JSX.Element,
  type: "critical" | "important" | "information" | "success",
) => {
  toast(<ToastNotification closeToast={true} text={content} type={type} />);
};

function App(props: any) {
  useTokenAutoRefresh();
  const [views, setViews] = useState<ReactFragment>([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const signalRConnection = useSelector((state: AppState) => {
    return state.reducer.signalR;
  });
  const orgData = useSelector((state: AppState) => {
    return state.reducer.orgData;
  });
  const downloadResponse = useSelector((state: AppState) => {
    return state.reducer.downloadResponse;
  });
  const downloadStatus = useSelector((state: AppState) => {
    return state.reducer.downloadStatus;
  });
  translateString = (value: string) => (
    <TranslationConsumer>
      {(translate: TFunction) => translate(value)}
    </TranslationConsumer>
  );
  const culture = (
    document.getElementById("userOptCulture") as HTMLInputElement
  )?.value.toLowerCase();
  const systemLanguage = culture ? culture : "enus";

  useEffect(() => {
    const loadTranslations = async () => {
      const language =
        systemLanguage !== "null" ? systemLanguage.replace("-", "") : "enus";
      const translationString: ITranslationObject =
        await importLocals(language);
      const renderHealthdashboardView: any = renderHealthDasboard(
        language,
        translationString.localizations,
      );
      setViews(renderHealthdashboardView);
    };

    const storageHandler = (e: any) => {
      if (e && e.key == "logged_user") {
        const isLoggedIn =
          localStorage.getItem("logged_user") === JSON.stringify(true);
        dispatch(hideLoader());
        dispatch(hideModel());
        resetAbortController();
        if (isLoggedIn) {
          dispatch(setLoggedIn(false));
          localStorage.setItem("logged_user", JSON.stringify(false));
          localStorage.setItem("logged_out_from", "App");
          window.close();
          navigate("/logout");
        } else {
          localStorage.setItem("logged_out_from", "App /");
          navigate("/");
        }
      } else if (e && e.key === "access_token") {
        console.log("signalRConnection access", signalRConnection, orgData);
      }
    };

    window.addEventListener("storage", storageHandler);
    window.addEventListener("custom-storage", storageHandler);
    loadTranslations();

    return () => {
      window.removeEventListener("storage", storageHandler);
      window.removeEventListener("custom-storage", storageHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      orgData &&
      orgData[0]?.name &&
      (!signalRConnection ||
        signalRConnection.state === HubConnectionState.Disconnected)
    ) {
      setUpSignalRConnection(orgData[0]?.name, dispatch);
    }
  }, [signalRConnection]);
  useEffect(() => {
    if (downloadStatus !== 0 && downloadStatus !== -1) {
      if (downloadStatus === 200) {
        handleDownload(
          downloadResponse?.data?.downloadUrl,
          downloadResponse?.data?.fileName,
        );
      } else if (downloadStatus === 409) {
        customToast(
          "Previous Download is in progress. Please try again after sometime",
          "critical",
        );
      } else if (downloadStatus === 413) {
        customToast(
          "Total size of files selected for download cannot exceed 2 GB",
          "critical",
        );
      } else {
        customToast(
          "Something went wrong while downloading, please try after sometime",
          "critical",
        );
      }

      // setLoadingPage(false);
      dispatch(setDownloadData({ data: null, status: 0 }));
    }
  }, [downloadResponse, downloadStatus]);
  const renderHealthDasboard = (lang: string, trans: any) => (
    <LocalizationConfig languageCode={lang}>
      <LocalizationProvider translations={trans}>
        <div>
          {/* <ToastProvider> */}
          <ToastMessageComponent />
          <RenderTosterProvider />
          <ToastContainer
            toastClassName="toast-notification-wrap"
            autoClose={false}
            position="top-right"
            hideProgressBar={true}
            draggable={false}
            closeButton={false}
            icon={false}
            // toastClassName='toast-notification-wrap'
          />
          {/* </ToastProvider> */}
        </div>
      </LocalizationProvider>
    </LocalizationConfig>
  );

  return (
    <Suspense fallback="Loading Local Files...">
      <div>{views}</div>
    </Suspense>
  );
}

export const RenderTosterProvider = () => {
  // const { addToast } = useToasts();
  // customToast = (
  //   content: string | ReactFragment,
  //   type: 'warning' | 'success' | 'error' | 'info'
  // ) =>
  //   addToast(content, {
  //     appearance: type,
  //     autoDismiss: true,
  //   });
  return (
    <Routes>
      {routesConfig?.map(({ ...props }, index) => (
        <Route
          key={index}
          Component={props.component}
          element={props.element}
          path={props.path}
        ></Route>
      ))}
    </Routes>
  );
};

export default App;
